/**
 * "Add channel" (phase 7b item 4): an email channel to groups (and users) of
 * the tenant, a Slack incoming webhook, or a generic webhook with its signing
 * secret. The URL and secret fields are passwords: they are sent once and the
 * server never shows them again. The form is local state (fresh on every
 * opening, as `CreateRundownDialog`); the request is the container's.
 */
import type { ChannelKindWire, CreateAlertChannelRequestWire } from '@acp/api-contract/alerts.js';
import { MIN_SIGNING_SECRET_CHARS, SLACK_HOOK_PREFIX } from '@acp/api-contract/alerts.js';
import { SEVERITIES, type Severity } from '@acp/tenant-alerts/catalog.js';
import { type FormEvent, useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldDescription, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';

export type ChannelChoice = { readonly id: string; readonly name: string };

export type AddChannelDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The tenant's groups, for an email channel's recipients (empty where the backend cannot list them). */
  readonly groups: readonly ChannelChoice[];
  readonly sites: readonly ChannelChoice[];
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (request: CreateAlertChannelRequestWire) => void;
};

const KINDS: readonly { readonly kind: ChannelKindWire; readonly label: string }[] = [
  { kind: 'email', label: 'Email' },
  { kind: 'slack', label: 'Slack (incoming webhook)' },
  { kind: 'webhook', label: 'Webhook (signed JSON)' },
];

/** What the form still lacks, or null when it can be sent. */
export function channelFormProblem(kind: ChannelKindWire, name: string, groupIds: readonly string[], url: string, secret: string): string | null {
  if (name.trim() === '') return 'Name the channel.';
  if (kind === 'email') return groupIds.length === 0 ? 'Pick at least one group to email.' : null;
  if (!url.startsWith('https://')) return 'The URL starts https://.';
  if (kind === 'slack' && !url.startsWith(SLACK_HOOK_PREFIX)) return `A Slack incoming webhook URL starts ${SLACK_HOOK_PREFIX}`;
  if (kind === 'webhook' && secret.length < MIN_SIGNING_SECRET_CHARS) return `The signing secret is at least ${String(MIN_SIGNING_SECRET_CHARS)} characters.`;
  return null;
}

function AddChannelForm({ onOpenChange, groups, sites, pending, error, onSubmit }: Omit<AddChannelDialogProps, 'open'>) {
  const [kind, setKind] = useState<ChannelKindWire>('email');
  const [name, setName] = useState('');
  const [minSeverity, setMinSeverity] = useState<Severity>('warning');
  const [groupIds, setGroupIds] = useState<readonly string[]>([]);
  const [siteId, setSiteId] = useState('');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const problem = channelFormProblem(kind, name, groupIds, url, secret);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (problem !== null || pending) return;
    onSubmit({
      name: name.trim(),
      kind,
      minSeverity,
      siteIds: siteId === '' ? null : [siteId],
      ...(kind === 'email' ? { recipients: { userIds: [], groupIds: [...groupIds] } } : { url: url.trim() }),
      ...(kind === 'webhook' ? { signingSecret: secret } : {}),
    });
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>Add a channel</DialogTitle>
        <DialogDescription>Where alerts are sent. A URL and a signing secret are stored securely and never shown again.</DialogDescription>
      </DialogHeader>
      <Field>
        <Label htmlFor="channel-kind">Kind</Label>
        <NativeSelect id="channel-kind" value={kind} onChange={(e) => setKind(KINDS.find((k) => k.kind === e.target.value)?.kind ?? 'email')} disabled={pending} data-testid="channel-kind">
          {KINDS.map((k) => (
            <option key={k.kind} value={k.kind}>
              {k.label}
            </option>
          ))}
        </NativeSelect>
      </Field>
      <Field>
        <Label htmlFor="channel-name">Name</Label>
        <Input id="channel-name" value={name} onChange={(e) => setName(e.target.value)} disabled={pending} maxLength={100} autoFocus data-testid="channel-name" />
      </Field>
      {kind === 'email' ? (
        <Field>
          <Label>Email the members of</Label>
          <div className="grid gap-1" data-testid="channel-groups">
            {groups.length === 0 ? <p className="text-sm text-muted-foreground">No groups to pick from.</p> : null}
            {groups.map((g) => (
              <label key={g.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  checked={groupIds.includes(g.id)}
                  disabled={pending}
                  onChange={(e) => setGroupIds(e.target.checked ? [...groupIds, g.id] : groupIds.filter((id) => id !== g.id))}
                />
                {g.name}
              </label>
            ))}
          </div>
        </Field>
      ) : (
        <Field>
          <Label htmlFor="channel-url">{kind === 'slack' ? 'Slack incoming webhook URL' : 'Webhook URL'}</Label>
          <Input id="channel-url" type="password" autoComplete="off" value={url} onChange={(e) => setUrl(e.target.value)} disabled={pending} data-testid="channel-url" />
        </Field>
      )}
      {kind === 'webhook' ? (
        <Field>
          <Label htmlFor="channel-secret">Signing secret</Label>
          <Input id="channel-secret" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} disabled={pending} data-testid="channel-secret" />
          <FieldDescription>Every delivery carries an HMAC-SHA256 signature made with it; your receiver checks it (docs/acp/alerts.md).</FieldDescription>
        </Field>
      ) : null}
      <div className="grid grid-cols-2 gap-4">
        <Field>
          <Label htmlFor="channel-severity">Send from severity</Label>
          <NativeSelect id="channel-severity" value={minSeverity} onChange={(e) => setMinSeverity(SEVERITIES.find((s) => s === e.target.value) ?? 'warning')} disabled={pending}>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field>
          <Label htmlFor="channel-site">Sites</Label>
          <NativeSelect id="channel-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} disabled={pending}>
            <option value="">Every site</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </NativeSelect>
        </Field>
      </div>
      <FieldError data-testid="channel-problem">{error ?? (name === '' ? null : problem)}</FieldError>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={problem !== null || pending} data-testid="add-channel-submit">
          {pending ? 'Adding…' : 'Add channel'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function AddChannelDialog({ open, onOpenChange, ...rest }: AddChannelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-channel-dialog">
        <AddChannelForm onOpenChange={onOpenChange} {...rest} />
      </DialogContent>
    </Dialog>
  );
}
